import { CategoryQuestions } from '../types';

export const yogaQuestions: CategoryQuestions = {
  title: 'Yoga Meditation Personalization',
  questions: {
    preferredStyle: {
      label: 'What type of yoga meditation appeals to you most?',
      type: 'radio',
      options: [
        'Gentle and restorative',
        'Energizing and strengthening',
        'Calming and meditative',
        'Balanced flow with transitions'
      ]
    },
    primaryGoal: {
      label: 'What would you like to focus on during your yoga meditation?',
      type: 'radio',
      options: [
        'Flexibility and gentle stretching',
        'Strength and stability',
        'Balance and coordination',
        'Breath awareness and pranayama',
        'Stress relief and relaxation',
        'Mental clarity and focus'
      ]
    },
    experienceLevel: {
      label: 'What is your yoga experience level?',
      type: 'radio',
      options: [
        'Complete beginner - new to yoga',
        'Beginner - some basic experience',
        'Intermediate - comfortable with basic poses',
        'Advanced - experienced practitioner'
      ]
    },
    physicalLimitations: {
      label: 'Do you have any physical limitations or areas that need special attention?',
      type: 'textarea',
      placeholder: 'e.g., knee issues, back sensitivity, wrist problems, or other considerations...'
    },
    beginningPosition: {
      label: 'What opening position feels most comfortable for you?',
      type: 'radio',
      options: [
        'Seated cross-legged (easy pose)',
        'Chair-supported seated position',
        'Child\'s pose (kneeling forward)',
        'Standing mountain pose',
        'Lying down (supine)'
      ]
    },
    endingPosition: {
      label: 'How would you prefer to end the session?',
      type: 'radio',
      options: [
        'Seated meditation position',
        'Savasana (lying down relaxation)',
        'Child\'s pose',
        'Gentle seated twist',
        'Heart-centered gratitude pose'
      ]
    },
    breathWork: {
      label: 'How comfortable are you with guided breathing techniques?',
      type: 'radio',
      options: [
        'Very comfortable - love detailed breath work',
        'Somewhat comfortable - simple instructions',
        'Beginner - need basic guidance',
        'Prefer minimal breath instruction'
      ]
    },
    sessionIntention: {
      label: 'What intention or goal do you have for this yoga meditation session?',
      type: 'textarea',
      placeholder: 'e.g., release tension, find inner peace, build confidence, connect with my body...'
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for you?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  groupTitle: 'Group Yoga Meditation Personalization',
  groupQuestions: {
    preferredStyle: {
      label: 'What type of group yoga meditation works best?',
      type: 'radio',
      options: [
        'Synchronized gentle flow for all',
        'Adaptive practice accommodating all levels',
        'Energizing group flow with modifications',
        'Calming meditative practice for group harmony'
      ]
    },
    primaryGoal: {
      label: 'What should your group focus on during yoga meditation?',
      type: 'radio',
      options: [
        'Building group connection and harmony',
        'Synchronized breathing and movement',
        'Individual practice in supportive group',
        'Collective stress relief and relaxation',
        'Group intention setting and manifestation'
      ]
    },
    groupExperienceLevel: {
      label: 'What is the overall yoga experience level of your group?',
      type: 'radio',
      options: [
        'All beginners - new to yoga',
        'Mixed experience levels',
        'Mostly intermediate practitioners',
        'All experienced - advanced group'
      ]
    },
    groupSpecialSubjects: {
      label: 'Is there a specific subject, theme, or reason you\'d like woven into this meditation?',
      type: 'textarea',
      placeholder: 'e.g., healing from loss, celebrating achievements, building confidence, processing stress, finding balance...'
    },
    sessionTiming: {
      label: 'When is this yoga meditation session taking place?',
      type: 'radio',
      options: [
        'Before yoga practice - preparation and centering',
        'After yoga practice - integration and reflection',
        'Standalone session - not connected to physical yoga'
      ]
    },
    groupBeginningPosition: {
      label: 'What opening position works best for your group?',
      type: 'radio',
      options: [
        'Seated circle formation',
        'Standing mountain pose together',
        'Individual comfortable positions',
        'Partner/buddy system positions',
        'Rows facing same direction',
        'Lying down (supine) in group formation'
      ]
    },
    instructorDirectives: {
      label: 'Instructor\'s special instructions or guidelines for this session',
      type: 'textarea',
      placeholder: 'Enter any specific instructions, themes, or focuses the instructor wants to emphasize during this group yoga meditation session...'
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your group?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  practitionerGroupTitle: 'Personalize Your Clients\' Group Yoga Meditation',
  practitionerGroupQuestions: {
    preferredStyle: {
      label: 'What type of group yoga meditation works best for your clients?',
      type: 'radio',
      options: [
        'Synchronized gentle flow for all',
        'Adaptive practice accommodating all levels',
        'Energizing group flow with modifications',
        'Calming meditative practice for group harmony'
      ]
    },
    primaryGoal: {
      label: 'What should your clients focus on during yoga meditation?',
      type: 'radio',
      options: [
        'Building group connection and harmony',
        'Synchronized breathing and movement',
        'Individual practice in supportive group',
        'Collective stress relief and relaxation',
        'Group intention setting and manifestation'
      ]
    },
    groupExperienceLevel: {
      label: 'What is the overall yoga experience level of your clients?',
      type: 'radio',
      options: [
        'All beginners - new to yoga',
        'Mixed experience levels',
        'Mostly intermediate practitioners',
        'All experienced - advanced group'
      ]
    },
    groupSpecialSubjects: {
      label: 'Is there a specific subject, theme, or reason you\'d like woven into this meditation for your clients?',
      type: 'textarea',
      placeholder: 'e.g., healing from loss, celebrating achievements, building confidence, processing stress, finding balance...'
    },
    sessionTiming: {
      label: 'When is this yoga meditation session taking place for your clients?',
      type: 'radio',
      options: [
        'Before yoga practice - preparation and centering',
        'After yoga practice - integration and reflection',
        'Standalone session - not connected to physical yoga'
      ]
    },
    groupBeginningPosition: {
      label: 'What opening position works best for your clients?',
      type: 'radio',
      options: [
        'Seated circle formation',
        'Standing mountain pose together',
        'Individual comfortable positions',
        'Partner/buddy system positions',
        'Rows facing same direction',
        'Lying down (supine) in group formation'
      ]
    },
    instructorDirectives: {
      label: 'Special instructions or guidelines for this session with your clients',
      type: 'textarea',
      placeholder: 'Enter any specific instructions, themes, or focuses you want to emphasize during this group yoga meditation session...'
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your clients?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  practitionerTitle: 'Personalize Your Client\'s Yoga Meditation Session',
  practitionerQuestions: {
    preferredStyle: {
      label: 'What type of yoga meditation appeals to your client most?',
      type: 'radio',
      options: [
        'Gentle and restorative',
        'Energizing and strengthening',
        'Calming and meditative',
        'Balanced flow with transitions'
      ]
    },
    primaryGoal: {
      label: 'What would your client like to focus on during their yoga meditation?',
      type: 'radio',
      options: [
        'Flexibility and gentle stretching',
        'Strength and stability',
        'Balance and coordination',
        'Breath awareness and pranayama',
        'Stress relief and relaxation',
        'Mental clarity and focus'
      ]
    },
    experienceLevel: {
      label: 'What is your client\'s yoga experience level?',
      type: 'radio',
      options: [
        'Complete beginner - new to yoga',
        'Beginner - some basic experience',
        'Intermediate - comfortable with basic poses',
        'Advanced - experienced practitioner'
      ]
    },
    physicalLimitations: {
      label: 'Does your client have any physical limitations or areas that need special attention?',
      type: 'textarea',
      placeholder: 'e.g., knee issues, back sensitivity, wrist problems, or other considerations...'
    },
    beginningPosition: {
      label: 'What opening position feels most comfortable for your client?',
      type: 'radio',
      options: [
        'Seated cross-legged (easy pose)',
        'Chair-supported seated position',
        'Child\'s pose (kneeling forward)',
        'Standing mountain pose',
        'Lying down (supine)'
      ]
    },
    endingPosition: {
      label: 'How would your client prefer to end the session?',
      type: 'radio',
      options: [
        'Seated meditation position',
        'Savasana (lying down relaxation)',
        'Child\'s pose',
        'Gentle seated twist',
        'Heart-centered gratitude pose'
      ]
    },
    breathWork: {
      label: 'How comfortable is your client with guided breathing techniques?',
      type: 'radio',
      options: [
        'Very comfortable - love detailed breath work',
        'Somewhat comfortable - simple instructions',
        'Beginner - need basic guidance',
        'Prefer minimal breath instruction'
      ]
    },
    sessionIntention: {
      label: 'What intention or goal does your client have for this yoga meditation session?',
      type: 'textarea',
      placeholder: 'e.g., release tension, find inner peace, build confidence, connect with body...'
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your client?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    }
  },
  giftTitle: 'Personalize Yoga Meditation for Your Loved One',
  giftQuestions: {
    preferredStyle: {
      label: 'What type of yoga meditation appeals to them most?',
      type: 'radio',
      options: ['Gentle and restorative', 'Energizing and strengthening', 'Calming and meditative', 'Balanced flow with transitions']
    },
    primaryGoal: {
      label: 'What would they like to focus on during yoga meditation?',
      type: 'radio',
      options: ['Flexibility and gentle stretching', 'Stress relief and relaxation', 'Mental clarity and focus', 'Breath awareness', 'Balance and grounding']
    },
    experienceLevel: {
      label: 'What is their yoga experience level?',
      type: 'radio',
      options: ['Complete beginner - new to yoga', 'Beginner - some basic experience', 'Intermediate - comfortable with basic poses', 'Advanced - experienced practitioner']
    },
    sessionIntention: {
      label: 'What intention would you like them to focus on?',
      type: 'textarea',
      placeholder: 'e.g., release tension, find inner peace, build confidence, connect with body...'
    },
    additionalNotes: {
      label: 'Anything else the AI should know to create a perfect session for them?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  }
};